import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Calendar, FileText, AlertTriangle, Bed, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ConsentDialog from './ConsentDialog';
import { Client } from '../types/shelter';
import { caseWorkers, mockClients } from '../data/mockData';
import { ClientPhotoUpload } from './ClientPhotoUpload';
import { DuplicateCheckDialog } from './DuplicateCheckDialog';
import { useDuplicateCheck } from '@/hooks/useDuplicateCheck';
import { useAuth } from '@/contexts/AuthContext';
import { useClients } from '@/hooks/useClients';
import { useRooms } from '@/hooks/useRooms';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface NewClientFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (client: Omit<Client, 'id'>) => void;
  siteId?: string;
}

const NewClientForm: React.FC<NewClientFormProps> = ({ isOpen, onClose, onSubmit, siteId }) => {
  const { } = useAuth();
  const { clients: allClients } = useClients();
  const { rooms: dbRooms } = useRooms();
  const { candidates, showDialog, setShowDialog, checkForDuplicates, logOverride, resetCheck } = useDuplicateCheck();
  const pendingSubmitRef = useRef<{ e: React.FormEvent; action: 'save' | 'checkin' | 'checkout' | 'reserve' } | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  // Fetch eligible Case Managers from DB. Includes anyone whose role enables
  // case management responsibilities (case_manager, manager, supervisor, program_director).
  const { data: dbCaseManagers = [] } = useQuery({
    queryKey: ['case-managers-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('role', ['case_manager', 'manager', 'supervisor', 'program_director']);
      if (error) throw error;
      const userIds = Array.from(new Set((data || []).map(r => r.user_id)));
      if (userIds.length === 0) return [];
      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      if (pErr) throw pErr;
      return (profiles || []).filter(p => p.full_name);
    },
    enabled: true,
  });

  const caseManagerOptions = dbCaseManagers.map(p => ({ id: p.id, full_name: p.full_name || 'Unknown' }));

  // Get available rooms from DB filtered by facility
  const facilityRooms = dbRooms.filter((r: any) => !siteId || r.site_id === siteId);
  
  // Count all clients assigned to a room (any non-archived status with a room_id)
  const roomOccupancy = new Map<string, number>();
  allClients
    .filter(c => c.room_id)
    .forEach(c => {
      roomOccupancy.set(c.room_id!, (roomOccupancy.get(c.room_id!) || 0) + 1);
    });

  const getAvailableRooms = () => {
    return facilityRooms
      .filter((r: any) => {
        const currentOccupancy = roomOccupancy.get(r.id) || 0;
        return currentOccupancy < (r.capacity || 1);
      })
      .sort((a: any, b: any) => a.room_number.localeCompare(b.room_number, undefined, { numeric: true }));
  };

  const [formData, setFormData] = useState({
    // Basic Profile Information
    firstName: '',
    lastName: '',
    age: '',
    phone: '',
    email: '',
    address: '',
    room: '',
    caseWorker: '',
    photo: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    
    // Food Allergies
    allergies: [] as string[],
    newAllergy: '',

    // Reminders
    reminders: [] as Array<{type: string, description: string, date: string}>,
    newReminderType: 'appointment',
    newReminderDescription: '',
    newReminderDate: '',
    
    // Service Records (will be initialized to 0)
    services: {
      daily: { date: new Date().toISOString().split('T')[0], metro: 0, laundry: 0, meal: 0, juice: 0 },
      total: { date: new Date().toISOString().split('T')[0], metro: 0, laundry: 0, meal: 0, juice: 0 }
    }
  });

  const doSubmit = (action: 'save' | 'checkin' | 'checkout' | 'reserve') => {
    // Find room number from room ID for display
    const selectedRoom = facilityRooms.find((r: any) => r.id === formData.room);
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    const newClient: any = {
      name: fullName,
      age: parseInt(formData.age),
      room: selectedRoom?.room_number || formData.room,
      roomId: formData.room, // Pass the room UUID
      siteId: siteId || undefined,
      status: action === 'checkin' ? 'checked-in' : action === 'checkout' ? 'checked-out' : action === 'reserve' ? 'reserved' : 'unbedded',
      caseWorker: formData.caseWorker,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      address: formData.address || undefined,
      allergies: formData.allergies.length > 0 ? formData.allergies : undefined,
      alerts: [],
      photo: formData.photo || undefined,
      reminders: formData.reminders,
      emergencyContact: formData.emergencyContactName ? {
        name: formData.emergencyContactName,
        relationship: formData.emergencyContactRelationship,
        phone: formData.emergencyContactPhone
      } : undefined,
      checkInTime: action === 'checkin' ? new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : undefined,
      checkOutTime: action === 'checkout' ? new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : undefined,
    };

    onSubmit(newClient);
    onClose();
    resetForm();
    resetCheck();
  };

  const handleSubmit = async (e: React.FormEvent, action: 'save' | 'checkin' | 'checkout' | 'reserve') => {
    e.preventDefault();

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();

    const hasDuplicates = await checkForDuplicates({ firstName, lastName });
    if (hasDuplicates) {
      pendingSubmitRef.current = { e, action };
      return; // Dialog will show
    }

    doSubmit(action);
  };

  const handleDuplicateOverride = async (reason: string) => {
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    await logOverride(reason, fullName, candidates.map(c => c.id));
    setShowDialog(false);

    if (pendingSubmitRef.current) {
      doSubmit(pendingSubmitRef.current.action);
      pendingSubmitRef.current = null;
    }
  };

  const handleDuplicateCancel = () => {
    setShowDialog(false);
    pendingSubmitRef.current = null;
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      age: '',
      phone: '',
      email: '',
      address: '',
      room: '',
      caseWorker: '',
      photo: '',
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      allergies: [],
      newAllergy: '',
      reminders: [],
      newReminderType: 'appointment',
      newReminderDescription: '',
      newReminderDate: '',
      services: {
        daily: { date: new Date().toISOString().split('T')[0], metro: 0, laundry: 0, meal: 0, juice: 0 },
        total: { date: new Date().toISOString().split('T')[0], metro: 0, laundry: 0, meal: 0, juice: 0 }
      }
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addAllergy = () => {
    if (formData.newAllergy.trim()) {
      setFormData(prev => ({
        ...prev,
        allergies: [...prev.allergies, prev.newAllergy.trim()],
        newAllergy: ''
      }));
    }
  };

  const removeAllergy = (index: number) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies.filter((_, i) => i !== index)
    }));
  };

  const addReminder = () => {
    if (formData.newReminderDescription.trim() && formData.newReminderDate) {
      setFormData(prev => ({
        ...prev,
        reminders: [...prev.reminders, {
          type: prev.newReminderType,
          description: prev.newReminderDescription.trim(),
          date: prev.newReminderDate
        }],
        newReminderDescription: '',
        newReminderDate: ''
      }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border border-border">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <DialogTitle className="text-xl font-semibold">New Client</DialogTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <form className="space-y-6">
          {/* Basic Profile Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="age">Age *</Label>
                <Input
                  id="age"
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="room">Bed Assignment *</Label>
                <Select value={formData.room} onValueChange={(value) => handleInputChange('room', value)}>
                  <SelectTrigger className="bg-background border border-border">
                    <div className="flex items-center gap-2">
                      <Bed className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="Select available bed" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-background border border-border z-50">
                    {getAvailableRooms().length === 0 ? (
                      <SelectItem value="no_rooms" disabled>
                        No beds available
                      </SelectItem>
                    ) : (
                      getAvailableRooms().map((room: any) => (
                        <SelectItem key={room.id} value={room.id} className="hover:bg-accent hover:text-accent-foreground">
                          {room.room_number}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {getAvailableRooms().length > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {getAvailableRooms().length} bed(s) available
                  </p>
                )}
              </div>
              <div>
                 <Label htmlFor="caseWorker">Case Manager</Label>
                <Select value={formData.caseWorker} onValueChange={(value) => handleInputChange('caseWorker', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={caseManagerOptions.length === 0 ? 'No case managers available' : 'Select case manager'} />
                  </SelectTrigger>
                  <SelectContent className="bg-background border border-border">
                   {caseManagerOptions.length === 0 ? (
                     <div className="px-2 py-3 text-sm text-muted-foreground">
                       No eligible staff found. Add a Case Manager, Manager, or Supervisor in Staff.
                     </div>
                   ) : (
                     caseManagerOptions.map((cm) => (
                       <SelectItem key={cm.id} value={cm.id}>
                         {cm.full_name}
                       </SelectItem>
                     ))
                   )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <Label>Client Photo</Label>
                <ClientPhotoUpload
                  clientName={`${formData.firstName} ${formData.lastName}`.trim()}
                  currentPhotoUrl={formData.photo}
                  onPhotoUploaded={(url) => handleInputChange('photo', url)}
                />
              </div>
            </div>
            
            {/* Emergency Contact */}
            <div className="col-span-2">
              <h4 className="text-md font-medium mb-3">Emergency Contact</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="emergencyContactName">Name</Label>
                  <Input
                    id="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                    placeholder="Emergency contact name"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyContactRelationship">Relationship</Label>
                  <Input
                    id="emergencyContactRelationship"
                    value={formData.emergencyContactRelationship}
                    onChange={(e) => handleInputChange('emergencyContactRelationship', e.target.value)}
                    placeholder="e.g., Mother, Father, Friend"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyContactPhone">Phone</Label>
                  <Input
                    id="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                    placeholder="Emergency contact phone"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Food Allergies */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Food Allergies
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={formData.newAllergy}
                  onChange={(e) => handleInputChange('newAllergy', e.target.value)}
                  placeholder="e.g., Peanuts, Dairy, Gluten"
                />
                <Button type="button" onClick={addAllergy}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
              {formData.allergies.length > 0 && (
                <div className="space-y-2">
                  {formData.allergies.map((allergy, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border border-border rounded-lg bg-destructive/10">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-destructive" />
                        <span className="font-medium">{allergy}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        type="button"
                        onClick={() => removeAllergy(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Alert Status Fields removed — alerts are not configured at intake. */}

          {/* Reminders */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Reminders
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reminder-type">Reminder Type</Label>
                  <Select value={formData.newReminderType} onValueChange={(value) => handleInputChange('newReminderType', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border">
                      <SelectItem value="appointment">Appointment</SelectItem>
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="document">Document Due</SelectItem>
                      <SelectItem value="followup">Follow-up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="reminder-date">Date & Time</Label>
                  <Input 
                    id="reminder-date" 
                    type="datetime-local"
                    value={formData.newReminderDate}
                    onChange={(e) => handleInputChange('newReminderDate', e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="reminder-description">Description</Label>
                <Input 
                  id="reminder-description" 
                  value={formData.newReminderDescription}
                  onChange={(e) => handleInputChange('newReminderDescription', e.target.value)}
                  placeholder="Reminder details..." 
                />
              </div>
              
              <Button type="button" onClick={addReminder} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Reminder
              </Button>

              {formData.reminders.length > 0 && (
                <div className="space-y-2">
                  <Label>Added Reminders</Label>
                  {formData.reminders.map((reminder, idx) => (
                    <div key={idx} className="p-3 border border-border rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{reminder.type} - {reminder.description}</p>
                          <p className="text-sm text-muted-foreground">{new Date(reminder.date).toLocaleString()}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              reminders: prev.reminders.filter((_, i) => i !== idx)
                            }));
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  disabled={!formData.firstName || !formData.lastName || !formData.age || !formData.room}
                >
                  Save Client
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setPendingAction('save'); setConsentOpen(true); }}>
                  Save Client
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setPendingAction('checkin'); setConsentOpen(true); }}>
                  Save & Check In
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setPendingAction('checkout'); setConsentOpen(true); }}>
                  Save & Check Out
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setPendingAction('reserve'); setConsentOpen(true); }}>
                  Reserve Bed
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <ConsentDialog
            open={consentOpen}
            onCancel={() => { setConsentOpen(false); setPendingAction(null); }}
            onConfirm={() => {
              setConsentOpen(false);
              if (pendingAction) {
                handleSubmit({ preventDefault: () => {} } as React.FormEvent, pendingAction as any);
                setPendingAction(null);
              }
            }}
          />
        </form>

        <DuplicateCheckDialog
          isOpen={showDialog}
          onClose={() => setShowDialog(false)}
          onOverride={handleDuplicateOverride}
          onCancel={handleDuplicateCancel}
          candidates={candidates}
          newClientName={`${formData.firstName} ${formData.lastName}`.trim()}
        />
      </DialogContent>
    </Dialog>
  );
};

export default NewClientForm;
